/**
 * The HTTP client.
 *
 * Every request is relative to `/api`, because the SPA and the API share one
 * origin in every environment - the shared load balancer in production, the
 * Vite proxy locally. There is deliberately no base-URL setting to configure.
 */
import { recordLog } from "@/shared/diagnostics";
import { isFramed, navigateTop, warmReturnPath } from "@/shared/embedding";

/** A failed request, carrying the status so callers can branch on 401. */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

/** Raised when IAP wants the browser to re-authenticate. */
export class SessionExpiredError extends ApiRequestError {
  constructor() {
    super("Your session has expired.", 401);
    this.name = "SessionExpiredError";
  }
}

/** The one path that can start an IAP sign-in. See {@link reauthenticate}. */
const SIGN_IN_PATH = "/api/signin";

/**
 * Navigates to the IAP-guarded sign-in route so the browser can authenticate.
 *
 * A reload will not do, and that was the bug: IAP only challenges a request it
 * reads as a *browser navigation*. Measured against the deployed development
 * host with no session cookie:
 *
 * ```
 * Sec-Fetch-Mode: navigate  ->  302 accounts.google.com/o/oauth2/v2/auth?...
 * Sec-Fetch-Mode: cors      ->  401 Invalid IAP credentials: empty token
 * ```
 *
 * Every call this client makes is the second kind, so no `fetch` can ever open
 * the sign-in. And the SPA shell is served from an anonymous bucket at `/`,
 * which IAP does not guard - so `location.reload()` re-fetched the same
 * unauthenticated shell, which called `/api/config`, which 401'd again. The
 * "Sign in again" button looped forever without a single request ever reaching
 * Google.
 *
 * A full-page navigation to a path under `/api` is navigation-shaped, so IAP
 * challenges it, and the route on the other side sends the user back here.
 *
 * **Inside BRUNT HQ's frame** the navigation has to be the whole tab's, because
 * Google's sign-in page refuses to be framed. It goes through `/__warm` on the
 * way back, which reopens this page inside HQ rather than on its own. A frame
 * may only move the whole tab while handling a click - which is the only way
 * this is ever called, from a "Sign in again" button.
 *
 * Deliberately takes no parameters: the views hand it straight to `onClick`,
 * so a parameter would receive the click event.
 */
export function reauthenticate(): void {
  // Preserve where they were, so signing in does not also lose their place.
  const returnTo = window.location.pathname + window.location.search;

  if (isFramed()) {
    // Absolute, so it means this app's /api/signin to the top window too.
    navigateTop(new URL(signInPath(warmReturnPath(returnTo)), window.location.origin).href);
    return;
  }

  window.location.assign(signInPath(returnTo));
}

/** The sign-in route, returning to `next` (a site-relative path) afterwards. */
function signInPath(next: string): string {
  return `${SIGN_IN_PATH}?next=${encodeURIComponent(next)}`;
}

/** Raised when the request never reached the API. */
export class NetworkError extends ApiRequestError {
  constructor(detail: string) {
    super(
      "Could not reach the API. It may be restarting, or the request was cut " +
        `short before a reply arrived. (${detail})`,
      0,
    );
    this.name = "NetworkError";
  }
}

/**
 * Performs a request, turning a transport failure into a useful message.
 *
 * A bare `fetch` rejection surfaces as "Failed to fetch", which tells nobody
 * anything. It happened for real here: the API returned 404 to a 3.4 MB upload
 * and closed the socket without draining the body, so the browser saw the
 * connection die mid-send. The server log said 404; the screen said "Failed to
 * fetch". Naming the shape of the failure is what makes the two reconcilable.
 */
async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (cause) {
    throw new NetworkError(cause instanceof Error ? cause.message : String(cause));
  }
}

/** The cheapest IAP-guarded GET, already the SPA's sign-in probe. */
const SESSION_PROBE_PATH = "/api/config";

/**
 * Works out why an upload died without a reply.
 *
 * IAP refuses a request with no live session at the edge, from its headers
 * alone. For a GET that 401 arrives intact. For a multi-megabyte upload the
 * browser is still sending when the 401 comes back, the stream is cut, and
 * `fetch` rejects with "Failed to fetch" - the 401 is lost. That is how an
 * expired session came to read as an outage on Run reconciliation, with
 * nothing in the Cloud Run log because the request never got that far.
 *
 * A GET with no body cannot be cut short that way, so asking again tells the
 * two apart. Only on failure: the happy path makes no extra request.
 *
 * @param error - What {@link send} threw for the upload.
 * @returns A SessionExpiredError when IAP has ended the session, otherwise
 *   `error` unchanged.
 */
async function explainCutShortUpload(error: unknown): Promise<unknown> {
  if (!(error instanceof NetworkError)) return error;

  try {
    const probe = await fetch(SESSION_PROBE_PATH, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (probe.status === 401 || probe.status === 403) {
      recordLog("warning", `upload cut short; ${SESSION_PROBE_PATH} returned ${probe.status}`);
      return new SessionExpiredError();
    }
  } catch {
    // The probe could not get through either: a genuine network failure.
  }
  return error;
}

/**
 * Turns a response into a parsed body, or into the right error.
 *
 * Shared by all three verbs, and the one place a failed request is recorded for
 * the diagnostics buffer. Recording here rather than at each call site is what
 * makes a problem report carry the API failure that prompted it: the reporter
 * describes a blank screen, and the issue already says which call 500'd.
 *
 * @param response - The response to interpret.
 * @param path - Path below `/api`, for the log line only.
 * @returns The parsed body.
 * @throws SessionExpiredError when IAP has ended the session.
 * @throws ApiRequestError for any other non-2xx response.
 */
async function parse<T>(response: Response, path: string): Promise<T> {
  if (response.status === 401 || response.status === 403) {
    recordLog("warning", `${path} returned ${response.status}; the IAP session has ended`);
    throw new SessionExpiredError();
  }

  if (!response.ok) {
    const message = await readErrorMessage(response);
    recordLog("error", `${path} returned ${response.status}: ${message}`);
    throw new ApiRequestError(message, response.status);
  }

  return (await response.json()) as T;
}

/**
 * Performs a GET and parses the JSON body.
 *
 * @param path - Path below `/api`, with a leading slash (e.g. `/config`).
 * @returns The parsed response body.
 * @throws SessionExpiredError when IAP has ended the session.
 * @throws ApiRequestError for any other non-2xx response.
 */
export async function apiGet<T>(path: string): Promise<T> {
  const response = await send(`/api${path}`, {
    headers: { Accept: "application/json" },
    // Same-origin, but explicit: IAP's session cookie must ride along.
    credentials: "same-origin",
  });

  return parse<T>(response, path);
}

/**
 * Posts multipart form data and parses the JSON response.
 *
 * No Content-Type header is set: the browser must add the multipart boundary
 * itself, and setting it by hand produces a request the server cannot parse.
 *
 * @param path - Path below `/api`, with a leading slash.
 * @param form - The form data to send.
 * @returns The parsed response body.
 * @throws SessionExpiredError when IAP has ended the session - including when
 *   IAP's early 401 cut the upload short, see {@link explainCutShortUpload}.
 * @throws NetworkError when the request never reached the API for any other reason.
 * @throws ApiRequestError for any other non-2xx response.
 */
export async function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  let response: Response;
  try {
    response = await send(`/api${path}`, {
      method: "POST",
      body: form,
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch (error) {
    // An expired session must still read as "sign in again", not an outage.
    throw await explainCutShortUpload(error);
  }

  return parse<T>(response, path);
}

/**
 * Posts a JSON body and parses the JSON response.
 *
 * @param path - Path below `/api`, with a leading slash.
 * @param body - Payload to serialize.
 * @returns The parsed response body.
 * @throws SessionExpiredError when IAP has ended the session.
 * @throws ApiRequestError for any other non-2xx response.
 */
export async function apiPostJson<T>(path: string, body: unknown): Promise<T> {
  const response = await send(`/api${path}`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "same-origin",
  });

  return parse<T>(response, path);
}

/** Pulls the server's message out of a failed response, falling back to status. */
async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: string } };
    if (body.error?.message) return body.error.message;
  } catch {
    // Not JSON - fall through to the generic message below.
  }
  return `The API returned ${response.status} ${response.statusText || ""}`.trim() + ".";
}
